import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

export type AccountType = 'Free' | 'Professional' | 'Premium';

export interface AccountData {
  accountType: AccountType;
  responseCount: number;
  registrationDate: string; // ISO date string
}

export interface InvitedFriend {
  email: string;
  registered: boolean;
}

type StoredAccountPayload = {
  accountData?: AccountData;
  invitedFriends?: InvitedFriend[];
};

const DEFAULT_RESPONSE_COUNT = 3;
const MAX_INVITED_FRIENDS = 10;
const BONUS_RESPONSES_PER_REGISTERED_FRIEND = 2;
const accountCache = new Map<string, AccountData>();
const invitedFriendsCache = new Map<string, InvitedFriend[]>();

function userDocRef(userId: string) {
  return doc(db, 'users', userId);
}

function createDefaultAccountData(): AccountData {
  return {
    accountType: 'Free',
    responseCount: DEFAULT_RESPONSE_COUNT,
    registrationDate: new Date().toISOString(),
  };
}

async function ensureFirestorePayload(userId: string): Promise<{ accountData: AccountData; invitedFriends: InvitedFriend[] }> {
  const snapshot = await getDoc(userDocRef(userId));
  const data = (snapshot.exists() ? (snapshot.data() as StoredAccountPayload) : {}) || {};

  let accountData = data.accountData ?? createDefaultAccountData();
  // Safety: never override live decremented counts.
  // Only fix obviously invalid/missing values.
  const hasInvalidResponseCount =
    typeof accountData.responseCount !== 'number' ||
    !Number.isFinite(accountData.responseCount) ||
    accountData.responseCount < 0;
  if (hasInvalidResponseCount) {
    accountData = {
      ...accountData,
      responseCount: DEFAULT_RESPONSE_COUNT,
    };
  }
  const invitedFriends = Array.isArray(data.invitedFriends) ? data.invitedFriends : [];

  accountCache.set(userId, accountData);
  invitedFriendsCache.set(userId, invitedFriends);

  // Backfill missing fields on the user document.
  if (
    !data.accountData ||
    !Array.isArray(data.invitedFriends) ||
    (typeof data.accountData?.responseCount !== 'number' ||
      !Number.isFinite(data.accountData?.responseCount as number) ||
      (data.accountData?.responseCount as number) < 0)
  ) {
    await setDoc(
      userDocRef(userId),
      { accountData, invitedFriends },
      { merge: true }
    );
  }

  return { accountData, invitedFriends };
}

export const initializeAccount = async (userId: string): Promise<AccountData> => {
  const accountData = createDefaultAccountData();
  accountCache.set(userId, accountData);
  if (!invitedFriendsCache.has(userId)) {
    invitedFriendsCache.set(userId, []);
  }
  await setDoc(userDocRef(userId), { accountData }, { merge: true });
  return accountData;
};

export const getAccountData = (userId: string): AccountData | null => {
  return accountCache.get(userId) ?? null;
};

export const refreshAccountData = async (userId: string): Promise<AccountData> => {
  const { accountData } = await ensureFirestorePayload(userId);
  return accountData;
};

export const getOrInitializeAccount = async (userId: string): Promise<AccountData> => {
  const cached = accountCache.get(userId);
  if (cached) return cached;
  const { accountData } = await ensureFirestorePayload(userId);
  return accountData;
};

export const decrementResponseCount = async (userId: string): Promise<number | null> => {
  const accountData = await getOrInitializeAccount(userId);
  const effective = getEffectiveResponseCount(userId);
  if (effective <= 0) return accountData.responseCount;
  if (accountData.accountType === 'Professional' || accountData.accountType === 'Premium') {
    return accountData.responseCount;
  }

  const next = {
    ...accountData,
    responseCount: Math.max(0, accountData.responseCount - 1),
  };
  accountCache.set(userId, next);
  await setDoc(userDocRef(userId), { accountData: next }, { merge: true });
  return next.responseCount;
};

export const updateAccountType = async (userId: string, accountType: AccountType): Promise<void> => {
  const accountData = await getOrInitializeAccount(userId);
  const next = {
    ...accountData,
    accountType,
    responseCount: accountType === 'Free' ? DEFAULT_RESPONSE_COUNT : accountData.responseCount,
  };
  accountCache.set(userId, next);
  await setDoc(userDocRef(userId), { accountData: next }, { merge: true });
};

export const getInvitedFriends = (userId: string): InvitedFriend[] => {
  return invitedFriendsCache.get(userId) ?? [];
};

export const refreshInvitedFriends = async (userId: string): Promise<InvitedFriend[]> => {
  const { invitedFriends } = await ensureFirestorePayload(userId);
  return invitedFriends;
};

async function saveInvitedFriends(userId: string, friends: InvitedFriend[]): Promise<void> {
  invitedFriendsCache.set(userId, friends);
  await setDoc(userDocRef(userId), { invitedFriends: friends }, { merge: true });
}

export const addInvitedFriend = async (userId: string, email: string): Promise<{ ok: boolean; error?: string }> => {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return { ok: false, error: 'Enter an email' };
  const friends = [...(await refreshInvitedFriends(userId))];
  if (friends.length >= MAX_INVITED_FRIENDS) return { ok: false, error: 'Maximum 10 friends' };
  if (friends.some((f) => f.email.toLowerCase() === normalized)) return { ok: false, error: 'Already invited' };
  friends.push({ email: normalized, registered: false });
  await saveInvitedFriends(userId, friends);
  return { ok: true };
};

export const removeInvitedFriend = async (userId: string, email: string): Promise<void> => {
  const friends = (await refreshInvitedFriends(userId)).filter(
    (f) => f.email.toLowerCase() !== email.trim().toLowerCase()
  );
  await saveInvitedFriends(userId, friends);
};

export const setFriendRegistered = async (userId: string, email: string): Promise<void> => {
  const normalized = email.trim().toLowerCase();
  const friends = (await refreshInvitedFriends(userId)).map((f) =>
    f.email.toLowerCase() === normalized ? { ...f, registered: true } : f
  );
  await saveInvitedFriends(userId, friends);
};

export const getRegisteredInvitesCount = (userId: string): number => {
  return getInvitedFriends(userId).filter((f) => f.registered).length;
};

export const getEffectiveResponseCount = (userId: string): number => {
  const accountData = getAccountData(userId);
  if (!accountData) return DEFAULT_RESPONSE_COUNT;
  if (accountData.accountType === 'Professional' || accountData.accountType === 'Premium') {
    return accountData.responseCount;
  }
  const registeredCount = getRegisteredInvitesCount(userId);
  return accountData.responseCount + registeredCount * BONUS_RESPONSES_PER_REGISTERED_FRIEND;
};

export const canUseApp = (userId: string): boolean => {
  const accountData = getAccountData(userId);
  if (!accountData) return true;
  if (accountData.accountType === 'Professional' || accountData.accountType === 'Premium') {
    return true;
  }
  return getEffectiveResponseCount(userId) > 0;
};
